/**
 * useCurrentAccountId — reactive read of the signed-in user's internal
 * Account.id (CUID).
 *
 * Backed by `accountAttributionStore.currentAccountId`, which is
 * populated by two converging paths:
 *
 *   1. `usePrimeAccountAttributionFromMembers` matches the auth user's
 *      email against a freshly-loaded members list and writes the
 *      matching accountId. This is the typical first path — the user
 *      opens any block whose collection has them as a member, members
 *      load, and the slot fills.
 *
 *   2. `userAnnotationStore.create` captures `created.ownerId` from
 *      the server response (the row was created by us, so its
 *      ownerId IS our accountId). Backstop for users who haven't
 *      opened a block whose members list contains them yet — as
 *      soon as they create any annotation, the slot fills.
 *
 * Either path is enough; they reconverge on the same value. The hook
 * returns `undefined` until at least one fires — callers that gate
 * UI on identity (e.g. the "delete this comment" button) should
 * treat `undefined` as "we don't know yet, don't show the affordance"
 * rather than "definitely not the owner".
 *
 * Why not a dedicated `/v1/account/me` endpoint? Because the data
 * is already available client-side via two paths — adding a third
 * server round-trip on every modal open would be redundant. The
 * tradeoff: the slot is empty for the brief window before either
 * path fires (typically a few ms after the modal opens), but the
 * delete button is gated on `currentAccountId !== undefined &&
 * currentAccountId === ownerId`, so the affordance correctly hides
 * during that window rather than flickering on a wrong value.
 */
import { useEffect } from "react";
import { accountAttributionStore } from "@/data/account-attribution-store";
import { useAccountMe } from "@/hooks/use-account-me";
export function useCurrentAccountId() {
    const currentAccountId = accountAttributionStore((s) => s.currentAccountId);
    const accountMe = useAccountMe();
    const bootstrapAccountId = accountMe.account?.id;
    // Backfill the attribution store's slot from bootstrap when members
    // priming hasn't run yet (e.g. private collections / stack children
    // loaded before any annotation create). This keeps ownership-gated
    // annotation interactions (select/drag/delete) available for rows
    // that already belong to the signed-in user.
    useEffect(() => {
        if (currentAccountId !== undefined)
            return;
        if (bootstrapAccountId === undefined)
            return;
        accountAttributionStore.getState().setCurrentAccountId(bootstrapAccountId);
    }, [currentAccountId, bootstrapAccountId]);
    return currentAccountId ?? bootstrapAccountId;
}
